/**
 * Inventory Reports — 8 report types with JSON preview + CSV download.
 * PDF/Excel deferred: the CSV opens directly in Excel/Sheets (UTF-8 BOM).
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Download, FileBarChart, Play } from "lucide-react";
import InventoryTabs from "@/components/inventory/InventoryTabs";

const REPORTS = [
  { id: "valuation",    label: "Inventory Valuation",  url: "/inventory/reports/valuation",    file: "viyora-valuation.csv" },
  { id: "low-stock",    label: "Low Stock",            url: "/inventory/reports/low-stock",    file: "viyora-low-stock.csv" },
  { id: "out-of-stock", label: "Out of Stock",         url: "/inventory/reports/out-of-stock", file: "viyora-out-of-stock.csv" },
  { id: "consumption",  label: "Consumption Report",   url: "/inventory/reports/consumption",  file: "viyora-consumption.csv",  daysDefault: 30 },
  { id: "expiry",       label: "Expiry Report",        url: "/inventory/reports/expiry",       file: "viyora-expiry.csv",       daysDefault: 60 },
  { id: "purchase",     label: "Purchase Report",      url: "/inventory/reports/purchase",     file: "viyora-purchases.csv",    daysDefault: 90 },
  { id: "movements",    label: "Movement Report",      url: "/inventory/reports/movements",    file: "viyora-movements.csv",    daysDefault: 30 },
  { id: "adjustments",  label: "Adjustment Report",    url: "/inventory/reports/adjustments",  file: "viyora-adjustments.csv",  daysDefault: 90 },
  { id: "audit",        label: "Audit Report",         url: "/inventory/reports/audit",        file: "viyora-audit.csv",        daysDefault: 90 },
  { id: "dead-stock",   label: "Dead Stock",           url: "/inventory/reports/dead-stock",   file: "viyora-dead-stock.csv",   daysDefault: 90 },
];

export default function InventoryReports() {
  const [active, setActive] = useState(REPORTS[0]);
  const [days, setDays] = useState(REPORTS[0].daysDefault || 30);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (report) => {
    setActive(report);
    setBusy(true);
    setRows([]); setMeta(null);
    try {
      const { data } = await api.get(report.url, {
        params: report.daysDefault ? { days } : {},
      });
      setRows(data.rows || []);
      setMeta({ ...data, rows: undefined });
    } catch (e) {
      toast.error(apiError(e, "Failed to run report"));
    } finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const resp = await api.get(active.url, {
        params: { format: "csv", ...(active.daysDefault ? { days } : {}) },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = active.file; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e, "CSV export failed")); }
    finally { setBusy(false); }
  };

  const cols = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="max-w-6xl mx-auto py-8 px-6 space-y-4" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="overline text-ink-soft">Inventory</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Reports</h1>
          <p className="mt-2 text-sm text-ink-mid">
            8 audit-ready views of your stock, procurement, and consumption.
            Preview here or export to CSV for accountants and stakeholders.
          </p>
        </div>
      </div>
      <InventoryTabs />

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => run(r)} data-testid={`report-${r.id}`}
                  className={`text-left rounded-2xl border p-4 transition-colors ${
                    active.id === r.id ? "border-pine bg-linen" : "border-[#E8EAE9] bg-white hover:border-pine/40"
                  }`}>
            <FileBarChart className="w-4 h-4 text-clay mb-2" />
            <div className="text-sm font-medium">{r.label}</div>
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm">{active.label}</div>
            {active.daysDefault && (
              <label className="text-xs text-ink-mid inline-flex items-center gap-1">
                Last
                <input type="number" min="1" max="365" value={days}
                       onChange={(e) => setDays(Number(e.target.value) || 30)}
                       className="w-16 px-2 py-1 rounded-lg border border-[#E8EAE9]"
                       data-testid="report-days" />
                days
              </label>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => run(active)} disabled={busy} data-testid="report-run"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-ink px-4 py-1.5 text-sm border border-[#E8EAE9] hover:border-pine">
              <Play className="w-3.5 h-3.5" /> Run
            </button>
            <button onClick={downloadCsv} disabled={busy || rows.length === 0} data-testid="report-download"
                    className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-1.5 text-sm hover:bg-pine/90 disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> Download CSV
            </button>
          </div>
        </div>

        {meta && (meta.total !== undefined || meta.total_spend !== undefined || meta.total_locked !== undefined) && (
          <div className="text-sm text-ink-mid">
            {meta.total !== undefined       && <>Total: <strong>₹{Number(meta.total).toLocaleString("en-IN")}</strong></>}
            {meta.total_spend !== undefined && <>Total spend: <strong>₹{Number(meta.total_spend).toLocaleString("en-IN")}</strong></>}
            {meta.total_locked !== undefined && <>Capital locked: <strong>₹{Number(meta.total_locked).toLocaleString("en-IN")}</strong></>}
          </div>
        )}

        {rows.length === 0 ? (
          <div className="py-8 text-center text-ink-soft text-sm" data-testid="report-empty">
            {busy ? "Running…" : "No data for this report."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="text-left text-ink-soft overline bg-linen/60">
                <tr>{cols.map(c => <th key={c} className="px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-t border-[#F0EEE8]">
                    {cols.map(c => <td key={c} className="px-3 py-2 whitespace-nowrap">{formatCell(r[c])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 200 && (
              <div className="text-center py-3 text-xs text-ink-soft">
                Showing 200 of {rows.length} rows — download CSV for the full report.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatCell(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (typeof v === "string" && v.length > 60) return v.slice(0, 60) + "…";
  return String(v);
}
